import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import './CitizenManagementPage.css';

const CitizenManagementPage = () => {
  const [cardNumbers, setCardNumbers] = useState([]);
  const [anuCardNumbers, setAnuCardNumbers] = useState([]);
  const [newCardNumber, setNewCardNumber] = useState('');
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [anuLoading, setAnuLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [validating, setValidating] = useState(false);
  const [validationResults, setValidationResults] = useState(null);
  const [lastValidationRun, setLastValidationRun] = useState(
    localStorage.getItem('lastValidationRun') || null
  );
  const [validationSchedule, setValidationSchedule] = useState(null);
  
  // Collapsible sections state
  const [isCitizenListExpanded, setIsCitizenListExpanded] = useState(false);
  const [isAnuListExpanded, setIsAnuListExpanded] = useState(false);

  useEffect(() => {
    fetchCardNumbers();
    fetchAnuCardNumbers();
    fetchValidationSchedule();
  }, []);

  const fetchCardNumbers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/citizens/card-numbers');
      setCardNumbers(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching card numbers:', err);
      setError('Failed to load citizen card numbers');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnuCardNumbers = async () => {
    try {
      setAnuLoading(true);
      const response = await api.get('/api/anu/card-numbers');
      setAnuCardNumbers(response.data);
    } catch (err) {
      console.error('Error fetching ANU card numbers:', err);
      setError('Failed to load ANU card numbers');
    } finally {
      setAnuLoading(false);
    }
  };

  const fetchValidationSchedule = async () => {
    try {
      const response = await api.get('/api/validation/schedule');
      setValidationSchedule(response.data.schedule);
    } catch (err) {
      console.error('Error fetching validation schedule:', err);
      // Don't show error, use defaults
      setValidationSchedule({
        syncTime: '02:00',
        validationTime: '03:00',
        delayMinutes: 60,
        timezone: 'EST'
      });
    }
  };

  const handleAddCardNumber = async (e) => {
    e.preventDefault();
    
    if (!newCardNumber.trim() || !newFirstName.trim() || !newLastName.trim()) {
      setError('Card number, first name, and last name are required');
      return;
    }

    try {
      setError('');
      setMessage('');
      
      await api.post('/api/citizens/card-numbers', {
        cardNumber: newCardNumber.trim(),
        firstName: newFirstName.trim(),
        lastName: newLastName.trim(),
        notes: notes.trim()
      });
      
      setMessage('Card number added successfully');
      setNewCardNumber('');
      setNewFirstName('');
      setNewLastName('');
      setNotes('');
      fetchCardNumbers();
    } catch (err) {
      console.error('Error adding card number:', err);
      setError(err.response?.data?.message || 'Failed to add card number');
    }
  };

  const handleDeleteCardNumber = async (id) => {
    if (!window.confirm('Are you sure you want to remove this card number from the citizen registry?')) {
      return;
    }

    try {
      await api.delete(`/api/citizens/card-numbers/${id}`);
      setMessage('Card number removed successfully');
      fetchCardNumbers();
    } catch (err) {
      console.error('Error deleting card number:', err);
      setError('Failed to delete card number');
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      await api.patch(`/api/citizens/card-numbers/${id}/status`, {
        isActive: !currentStatus
      });
      setMessage('Status updated successfully');
      fetchCardNumbers();
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update status');
    }
  };

  const handleValidateAll = async () => {
    if (!window.confirm('This will validate all users against the citizen registry. Users with matching card numbers will be promoted to Citizen status. Citizens without matching card numbers will be demoted to User status. Staff and Admin users will not be affected. Continue?')) {
      return;
    }

    try {
      setValidating(true);
      setError('');
      setMessage('');
      setValidationResults(null);
      
      const response = await api.post('/api/citizens/validate-all');
      
      const now = new Date().toISOString();
      localStorage.setItem('lastValidationRun', now);
      setLastValidationRun(now);
      setValidationResults(response.data);
      setMessage(response.data.message);
    } catch (err) {
      console.error('Error validating users:', err);
      setError(err.response?.data?.message || 'Failed to validate users');
    } finally {
      setValidating(false);
    }
  };

  const handleDeleteAll = async () => {
    const confirmText = 'DELETE ALL';
    const userInput = window.prompt(
      `⚠️ WARNING: This will permanently delete ALL ${cardNumbers.length} citizen card numbers from the registry!\n\n` +
      `This action CANNOT be undone. All users with "Citizen" status may need to be re-validated.\n\n` +
      `Type "${confirmText}" (without quotes) to confirm:`
    );

    if (userInput !== confirmText) {
      if (userInput !== null) {
        setError('Deletion cancelled - confirmation text did not match');
      }
      return;
    }

    try {
      setError('');
      setMessage('');
      
      const response = await api.delete('/api/citizens/card-numbers/all');
      
      setMessage(`Successfully deleted all citizen card numbers. ${response.data.deletedCount} records removed.`);
      fetchCardNumbers();
      setValidationResults(null);
    } catch (err) {
      console.error('Error deleting all card numbers:', err);
      setError(err.response?.data?.message || 'Failed to delete all card numbers');
    }
  };

  const handleDeleteAnuCard = async (id) => {
    if (!window.confirm('Are you sure you want to remove this ANU card number from the registry?')) {
      return;
    }

    try {
      await api.delete(`/api/anu/card-numbers/${id}`);
      setMessage('ANU card number removed successfully');
      fetchAnuCardNumbers();
    } catch (err) {
      console.error('Error deleting ANU card number:', err);
      setError('Failed to delete ANU card number');
    }
  };

  const handleToggleAnuStatus = async (id, currentStatus) => {
    try {
      await api.patch(`/api/anu/card-numbers/${id}/status`, {
        isActive: !currentStatus
      });
      setMessage('ANU card status updated successfully');
      fetchAnuCardNumbers();
    } catch (err) {
      console.error('Error updating ANU card status:', err);
      setError('Failed to update ANU card status');
    }
  };

  const handleDeleteAllAnuCards = async () => {
    const confirmText = 'DELETE ALL';
    const userInput = window.prompt(
      `⚠️ WARNING: This will permanently delete ALL ${anuCardNumbers.length} ANU card numbers from the registry!\n\n` +
      `This action CANNOT be undone.\n\n` +
      `Type "${confirmText}" (without quotes) to confirm:`
    );

    if (userInput !== confirmText) {
      if (userInput !== null) {
        setError('Deletion cancelled - confirmation text did not match');
      }
      return;
    }

    try {
      setError('');
      setMessage('');
      
      const response = await api.delete('/api/anu/card-numbers/all');
      
      setMessage(`Successfully deleted all ANU card numbers. ${response.data.deletedCount} records removed.`);
      fetchAnuCardNumbers();
    } catch (err) {
      console.error('Error deleting all ANU card numbers:', err);
      setError(err.response?.data?.message || 'Failed to delete all ANU card numbers');
    }
  };

  return (
    <div className="citizen-management-page">
      <div className="page-header">
        <h2>Citizen Registry Management</h2>
        <p>Manage the list of authorized citizen card numbers and validate user accounts</p>
      </div>

      {/* Validation Section */}
      <div className="validation-section card">
        <h3>User Validation</h3>
        <p>Click the button below to validate all users against the citizen registry. This will automatically update user types based on their card numbers.</p>
        
        <div className="validation-timing-info">
          <h4>⏱️ Automated Validation Schedule</h4>
          <p>The server automatically validates all users against the citizen registry nightly:</p>
          <div className="timing-settings">
            <div className="setting-item">
              <span className="setting-label">Registry Sync Time:</span>
              <span className="setting-value">
                Daily at {validationSchedule?.syncTime || '02:00'} {validationSchedule?.timezone || 'EST'}
              </span>
            </div>
            <div className="setting-item">
              <span className="setting-label">Validation Run Time:</span>
              <span className="setting-value">
                Daily at {validationSchedule?.validationTime || '03:00'} {validationSchedule?.timezone || 'EST'}
              </span>
            </div>
            <div className="setting-item">
              <span className="setting-label">Auto-Validate on Card Save:</span>
              <span className="setting-value status-active">✅ Enabled — runs instantly when a user saves their card number</span>
            </div>
            <div className="setting-item">
              <span className="setting-label">Last Manual / Cron Run:</span>
              <span className="setting-value">
                {lastValidationRun
                  ? new Date(lastValidationRun).toLocaleString()
                  : 'No manual run this session'}
              </span>
            </div>
            <div className="setting-item">
              <span className="setting-label">Configuration:</span>
              <span className="setting-value">backend/.env (REGISTRY_SYNC_TIME, VALIDATION_RUN_TIME)</span>
            </div>
          </div>
          <p className="timing-note">
            💡 To modify the nightly schedule, update <code>VALIDATION_RUN_TIME</code> in <code>backend/.env</code> and restart the server.
            Manual validation can be triggered anytime using the button below.
          </p>
        </div>
        
        <button 
          className="btn btn-primary validate-btn"
          onClick={handleValidateAll}
          disabled={validating}
        >
          {validating ? 'Validating...' : 'Validate All Users'}
        </button>

        {validationResults && (
          <div className="validation-results">
            <h4>Validation Results</h4>
            <div className="results-summary">
              <div className="result-stat">
                <span className="stat-label">Promoted to Citizen:</span>
                <span className="stat-value promoted">{validationResults.promotedToCitizen ?? 0}</span>
              </div>
              <div className="result-stat">
                <span className="stat-label">Demoted to User:</span>
                <span className="stat-value demoted">{validationResults.demotedToUser ?? 0}</span>
              </div>
              <div className="result-stat">
                <span className="stat-label">Flagged Card Issues:</span>
                <span className="stat-value demoted">{validationResults.flaggedCardIssues ?? 0}</span>
              </div>
              <div className="result-stat">
                <span className="stat-label">Cleared Card Issues:</span>
                <span className="stat-value promoted">{validationResults.clearedCardIssues ?? 0}</span>
              </div>
              <div className="result-stat">
                <span className="stat-label">Total Processed:</span>
                <span className="stat-value">{validationResults.totalProcessed ?? 0}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add Card Number Form */}
      <div className="add-card-section card">
        <h3>Add Citizen Card Number</h3>
        <form onSubmit={handleAddCardNumber}>
          <div className="form-group">
            <label htmlFor="cardNumber">Card Number</label>
            <input
              type="text"
              id="cardNumber"
              value={newCardNumber}
              onChange={(e) => setNewCardNumber(e.target.value)}
              placeholder="Enter card number"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="firstName">First Name</label>
            <input
              type="text"
              id="firstName"
              value={newFirstName}
              onChange={(e) => setNewFirstName(e.target.value)}
              placeholder="Enter first name"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="lastName">Last Name</label>
            <input
              type="text"
              id="lastName"
              value={newLastName}
              onChange={(e) => setNewLastName(e.target.value)}
              placeholder="Enter last name"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="notes">Notes (Optional)</label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about this card number"
              rows="3"
            />
          </div>

          <button type="submit" className="btn btn-primary">
            Add Card Number
          </button>
        </form>
      </div>

      {/* Messages */}
      {error && <div className="alert alert-error">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      {/* Card Numbers List */}
      <div className="card-numbers-section card">
        <div className="section-header" onClick={() => setIsCitizenListExpanded(!isCitizenListExpanded)} style={{ cursor: 'pointer' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '1.2em' }}>{isCitizenListExpanded ? '▼' : '▶'}</span>
            <h3>Registered Citizen Card Numbers ({cardNumbers.length})</h3>
          </div>
          {cardNumbers.length > 0 && (
            <button 
              className="btn btn-danger btn-sm"
              onClick={(e) => { e.stopPropagation(); handleDeleteAll(); }}
              title="Delete all citizen card numbers"
            >
              Delete All
            </button>
          )}
        </div>
        
        {isCitizenListExpanded && (
          loading ? (
            <div className="loading">Loading card numbers...</div>
          ) : cardNumbers.length === 0 ? (
            <div className="no-data">No citizen card numbers registered yet.</div>
          ) : (
            <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Card Number</th>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th>Notes</th>
                  <th>Status</th>
                  <th>Added Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {cardNumbers.map((record) => (
                  <tr key={record._id} className={!record.isActive ? 'inactive-row' : ''}>
                    <td className="card-number-cell">
                      <span className="card-number">{record.cardNumber}</span>
                    </td>
                    <td>{record.firstName || '-'}</td>
                    <td>{record.lastName || '-'}</td>
                    <td>{record.notes || '-'}</td>
                    <td>
                      <span className={`status-badge ${record.isActive ? 'active' : 'inactive'}`}>
                        {record.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>{new Date(record.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className={`btn btn-sm ${record.isActive ? 'btn-warning' : 'btn-success'}`}
                          onClick={() => handleToggleStatus(record._id, record.isActive)}
                          title={record.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {record.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteCardNumber(record._id)}
                          title="Delete"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )
        )}
      </div>

      {/* ANU Card Numbers List */}
      <div className="card-numbers-section card">
        <div className="section-header" onClick={() => setIsAnuListExpanded(!isAnuListExpanded)} style={{ cursor: 'pointer' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '1.2em' }}>{isAnuListExpanded ? '▼' : '▶'}</span>
            <h3>Annuitant of Robinson-Superior Treaty (ANU) Card Numbers ({anuCardNumbers.length})</h3>
          </div>
          {anuCardNumbers.length > 0 && (
            <button 
              className="btn btn-danger btn-sm"
              onClick={(e) => { e.stopPropagation(); handleDeleteAllAnuCards(); }}
              title="Delete all ANU card numbers"
            >
              Delete All
            </button>
          )}
        </div>
        
        {isAnuListExpanded && (
          anuLoading ? (
            <div className="loading">Loading ANU card numbers...</div>
          ) : anuCardNumbers.length === 0 ? (
            <div className="no-data">No ANU card numbers registered yet.</div>
          ) : (
            <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Card Number</th>
                  <th>Notes</th>
                  <th>Status</th>
                  <th>Added Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {anuCardNumbers.map((record) => (
                  <tr key={record._id} className={!record.isActive ? 'inactive-row' : ''}>
                    <td className="card-number-cell">
                      <span className="card-number">{record.cardNumber}</span>
                    </td>
                    <td>{record.notes || '-'}</td>
                    <td>
                      <span className={`status-badge ${record.isActive ? 'active' : 'inactive'}`}>
                        {record.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>{new Date(record.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className={`btn btn-sm ${record.isActive ? 'btn-warning' : 'btn-success'}`}
                          onClick={() => handleToggleAnuStatus(record._id, record.isActive)}
                          title={record.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {record.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteAnuCard(record._id)}
                          title="Delete"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )
        )}
      </div>

      {/* Information Box */}
      <div className="info-box card">
        <h4>ℹ️ How It Works</h4>
        <ul>
          <li>Add card numbers to the citizen registry above</li>
          <li>Click "Validate All Users" to check all users against the registry</li>
          <li>Users with matching card numbers will be automatically promoted to "Citizen" status</li>
          <li>Citizens without matching card numbers will be demoted to "User" status</li>
          <li>Staff and Admin accounts are never affected by validation</li>
          <li>You can deactivate card numbers without deleting them</li>
          <li>Only active card numbers are used during validation</li>
        </ul>
      </div>
    </div>
  );
};

export default CitizenManagementPage;
